import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus } from "lucide-react";
import {
  prescriptionTemplateQueryKeys,
  prescriptionTemplatesApi,
} from "../../../shared/api/prescriptionTemplates";
import { medicinesApi } from "../../../shared/api/medicines";
import type {
  PrescriptionTemplateItemDto,
  PrescriptionTemplateResponse,
  PrescriptionTemplateSaveRequest,
} from "../../../shared/types/prescriptionTemplate";
import type {
  DrugRouteResponse,
  MealTiming,
  MedicineSummaryResponse,
} from "../../../shared/types/medicine";
import { mealTimingLabel } from "../../../shared/format";
import { apiError } from "../../../shared/api/client";
import { MedicinePicker } from "./MedicinePicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

interface Props {
  mode: "create" | "edit";
  initial?: PrescriptionTemplateResponse | null;
}

type ItemRow = PrescriptionTemplateItemDto & {
  uiKey: string;
  medicineName: string;
  medicineUnit: string;
  medicineStrength?: string | null;
};

let _uiKeyCounter = 1;
function nextKey() {
  return `row-${_uiKeyCounter++}`;
}

const nativeSelectClass =
  "flex h-9 w-full items-center rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

export function PrescriptionTemplateForm({ mode, initial }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [code, setCode] = useState(initial?.code ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [diagnosis, setDiagnosis] = useState(initial?.diagnosis ?? "");
  const [icd10, setIcd10] = useState(initial?.icd10Code ?? "");
  const [durationDays, setDurationDays] = useState(initial?.durationDays ?? 5);
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [visible, setVisible] = useState(initial?.visible ?? true);
  const [items, setItems] = useState<ItemRow[]>(() =>
    (initial?.items ?? []).map((it) => ({
      uiKey: nextKey(),
      medicineId: it.medicineId,
      medicineName: it.medicineName,
      medicineUnit: it.medicineUnit,
      medicineStrength: it.medicineStrength,
      quantity: it.quantity,
      doseMorning: it.doseMorning,
      doseNoon: it.doseNoon,
      doseAfternoon: it.doseAfternoon,
      doseEvening: it.doseEvening,
      doseUnit: it.doseUnit ?? it.medicineUnit,
      route: it.route ?? "ORAL",
      mealTiming: it.mealTiming,
      note: it.note,
    }))
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTargetIndex, setPickerTargetIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const routes = useQuery({
    queryKey: ["drug-routes"],
    queryFn: () => medicinesApi.listRoutes(),
  });

  const save = useMutation({
    mutationFn: async (req: PrescriptionTemplateSaveRequest) =>
      mode === "create"
        ? prescriptionTemplatesApi.create(req)
        : prescriptionTemplatesApi.update(initial!.id, req),
    // #18603: lưu thành công → về Danh sách toa thuốc mẫu (không ở lại trang tạo/sửa).
    // Lỗi/validation vẫn giữ nguyên màn hình (xử lý ở onError + validate()).
    //
    // #18711: phải invalidate cache TRƯỚC khi điều hướng. Client đặt staleTime = 30s (main.tsx),
    // nên nếu không invalidate thì cả query list lẫn query detail vẫn được coi là "còn tươi" và
    // React Query trả lại bản cache cũ — mở lại toa vừa sửa sẽ thiếu đúng thuốc vừa thêm, cho tới
    // khi hết 30s hoặc người dùng reload trang (reload = cache mới hoàn toàn). Key `all` là tiền tố
    // của cả list lẫn detail nên một lệnh này phủ hết; cũng vá luôn trường hợp tạo mới không thấy
    // toa trong danh sách.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: prescriptionTemplateQueryKeys.all });
      navigate("/prescriptions/templates");
    },
    onError: (e) => setError(apiError(e).detail),
  });

  function pickMedicine(medicine: MedicineSummaryResponse) {
    const idx = pickerTargetIndex;
    setItems((cur) => {
      if (idx == null) {
        return [
          ...cur,
          {
            uiKey: nextKey(),
            medicineId: medicine.id,
            medicineName: medicine.name,
            medicineUnit: medicine.unit,
            medicineStrength: medicine.strength,
            quantity: 0,
            doseMorning: 0,
            doseNoon: 0,
            doseAfternoon: 0,
            doseEvening: 0,
            doseUnit: medicine.unit,
            route: "ORAL",
            mealTiming: null,
            note: null,
          },
        ];
      }
      return cur.map((row, i) =>
        i === idx
          ? {
              ...row,
              medicineId: medicine.id,
              medicineName: medicine.name,
              medicineUnit: medicine.unit,
              medicineStrength: medicine.strength,
              doseUnit: row.doseUnit || medicine.unit,
            }
          : row
      );
    });
    setPickerTargetIndex(null);
  }

  function updateItem(idx: number, patch: Partial<ItemRow>) {
    setItems((cur) => cur.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function removeItem(idx: number) {
    setItems((cur) => cur.filter((_, i) => i !== idx));
  }

  function moveItem(idx: number, dir: -1 | 1) {
    setItems((cur) => {
      const next = [...cur];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return cur;
      [next[idx], next[target]] = [next[target]!, next[idx]!];
      return next;
    });
  }

  function recommendedQuantity(row: ItemRow): number {
    const perDay =
      (row.doseMorning ?? 0) +
      (row.doseNoon ?? 0) +
      (row.doseAfternoon ?? 0) +
      (row.doseEvening ?? 0);
    return Math.ceil(perDay * durationDays);
  }

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (mode === "create" && !code.trim()) errs.code = "Mã toa mẫu không được để trống";
    if (!name.trim()) errs.name = "Tên toa mẫu không được để trống";
    if (durationDays < 1 || durationDays > 90) errs.durationDays = "Số ngày phải 1–90";
    if (items.length === 0) errs._items = "Toa mẫu phải có ít nhất 1 thuốc";
    items.forEach((it, i) => {
      if (!it.medicineId) errs[`item_${i}_medicine`] = "Chưa chọn thuốc";
      if ((it.quantity ?? 0) < 0) errs[`item_${i}_qty`] = "Số lượng ≥ 0";
    });
    return errs;
  }

  function onSubmit() {
    setError(null);
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const payload: PrescriptionTemplateSaveRequest = {
      code: mode === "create" ? code.trim() : undefined,
      name: name.trim(),
      diagnosis: diagnosis.trim() || null,
      icd10Code: icd10.trim() || null,
      durationDays,
      notes: notes.trim() || null,
      visible,
      items: items.map((it) => ({
        medicineId: it.medicineId,
        quantity: it.quantity ?? 0,
        doseMorning: it.doseMorning ?? 0,
        doseNoon: it.doseNoon ?? 0,
        doseAfternoon: it.doseAfternoon ?? 0,
        doseEvening: it.doseEvening ?? 0,
        doseUnit: it.doseUnit ?? null,
        route: it.route ?? null,
        mealTiming: it.mealTiming ?? null,
        note: it.note ?? null,
      })),
    };
    save.mutate(payload);
  }

  const totalItems = useMemo(() => items.length, [items]);

  return (
    <div className="space-y-6 font-sans">
      <Card>
        <CardContent className="space-y-4 p-6">
          <h3 className="text-base font-semibold text-foreground">Thông tin chung</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {mode === "create" && (
              <div className="space-y-1.5">
                <Label htmlFor="tpl-code">Mã toa mẫu *</Label>
                <Input
                  id="tpl-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={64}
                  placeholder="VD: TM-VND-01"
                  autoFocus
                  aria-invalid={Boolean(fieldErrors.code)}
                />
                {fieldErrors.code && (
                  <p className="text-sm text-destructive">{fieldErrors.code}</p>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="tpl-name">Tên toa mẫu *</Label>
              <Input
                id="tpl-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={256}
                placeholder="VD: Viêm Niêm mạc dạ dày"
                autoFocus={mode === "edit"}
                aria-invalid={Boolean(fieldErrors.name)}
              />
              {fieldErrors.name && (
                <p className="text-sm text-destructive">{fieldErrors.name}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-icd">Mã ICD-10</Label>
              <Input
                id="tpl-icd"
                type="text"
                value={icd10}
                onChange={(e) => setIcd10(e.target.value.toUpperCase())}
                maxLength={16}
                placeholder="VD: K29.7"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-dx">Chẩn đoán</Label>
              <Input
                id="tpl-dx"
                type="text"
                value={diagnosis}
                onChange={(e) => setDiagnosis(e.target.value)}
                maxLength={256}
                placeholder="Viêm niêm mạc dạ dày"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-days">Số ngày *</Label>
              <Input
                id="tpl-days"
                type="number"
                inputMode="numeric"
                min={1}
                max={90}
                value={durationDays}
                onChange={(e) =>
                  setDurationDays(Math.max(1, Math.min(90, Number(e.target.value) || 1)))
                }
                className="w-28"
                aria-invalid={Boolean(fieldErrors.durationDays)}
              />
              {fieldErrors.durationDays && (
                <p className="text-sm text-destructive">{fieldErrors.durationDays}</p>
              )}
            </div>
            <div className="flex items-center gap-2 sm:pt-7">
              <Checkbox
                id="tpl-visible"
                checked={visible}
                onCheckedChange={(v) => setVisible(v === true)}
              />
              <Label htmlFor="tpl-visible" className="cursor-pointer font-normal">
                Hiển thị trong dropdown kê đơn
              </Label>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tpl-notes">Ghi chú toa mẫu</Label>
            <Textarea
              id="tpl-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={4000}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-foreground">
              Danh sách thuốc{" "}
              <span className="text-sm font-normal text-muted-foreground">
                ({totalItems} mục)
              </span>
            </h3>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPickerTargetIndex(null);
                setPickerOpen(true);
              }}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Thêm thuốc
            </Button>
          </div>

          {fieldErrors._items && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {fieldErrors._items}
            </div>
          )}

          {items.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Chưa có thuốc nào. Bấm "Thêm thuốc" để bắt đầu.
            </p>
          )}

          <div className="flex flex-col gap-4">
            {items.map((row, idx) => (
              <ItemEditor
                key={row.uiKey}
                index={idx}
                row={row}
                routes={routes.data ?? []}
                recommendedQuantity={recommendedQuantity(row)}
                onChange={(patch) => updateItem(idx, patch)}
                onRemove={() => removeItem(idx)}
                onPickMedicine={() => {
                  setPickerTargetIndex(idx);
                  setPickerOpen(true);
                }}
                onMoveUp={idx > 0 ? () => moveItem(idx, -1) : undefined}
                onMoveDown={idx < items.length - 1 ? () => moveItem(idx, 1) : undefined}
                hasError={
                  Boolean(fieldErrors[`item_${idx}_medicine`]) ||
                  Boolean(fieldErrors[`item_${idx}_qty`])
                }
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/prescriptions/templates")}
        >
          Hủy
        </Button>
        <Button type="button" onClick={onSubmit} disabled={save.isPending}>
          {save.isPending
            ? "Đang lưu…"
            : mode === "create"
              ? "Tạo toa mẫu"
              : "Lưu thay đổi"}
        </Button>
      </div>

      <MedicinePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={pickMedicine}
      />
    </div>
  );
}

function ItemEditor({
  index,
  row,
  routes,
  recommendedQuantity,
  onChange,
  onRemove,
  onPickMedicine,
  onMoveUp,
  onMoveDown,
  hasError,
}: {
  index: number;
  row: ItemRow;
  routes: DrugRouteResponse[];
  recommendedQuantity: number;
  onChange: (patch: Partial<ItemRow>) => void;
  onRemove: () => void;
  onPickMedicine: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  hasError: boolean;
}) {
  return (
    <div
      className={`rounded-md border bg-muted/40 p-4 ${
        hasError ? "border-destructive" : "border-border"
      }`}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
            {index + 1}
          </span>
          {row.medicineId ? (
            <div className="flex flex-wrap items-center gap-1">
              <strong className="text-foreground">{row.medicineName}</strong>
              {row.medicineStrength && (
                <span className="text-muted-foreground">· {row.medicineStrength}</span>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-1 h-auto px-2 py-0.5 text-xs"
                onClick={onPickMedicine}
              >
                Đổi thuốc
              </Button>
            </div>
          ) : (
            <Button type="button" onClick={onPickMedicine}>
              Chọn thuốc…
            </Button>
          )}
        </div>
        <div className="flex gap-1">
          {onMoveUp && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onMoveUp}
              title="Lên"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          )}
          {onMoveDown && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onMoveDown}
              title="Xuống"
            >
              <ArrowDown className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="text-xs"
            onClick={onRemove}
          >
            Xoá
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <div className="space-y-1.5">
          <Label>Liều Sáng</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            value={row.doseMorning ?? 0}
            onChange={(e) => onChange({ doseMorning: Number(e.target.value) || 0 })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Liều Trưa</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            value={row.doseNoon ?? 0}
            onChange={(e) => onChange({ doseNoon: Number(e.target.value) || 0 })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Liều Chiều</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            value={row.doseAfternoon ?? 0}
            onChange={(e) => onChange({ doseAfternoon: Number(e.target.value) || 0 })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Liều Tối</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            value={row.doseEvening ?? 0}
            onChange={(e) => onChange({ doseEvening: Number(e.target.value) || 0 })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>ĐV liều</Label>
          <Input
            type="text"
            value={row.doseUnit ?? ""}
            onChange={(e) => onChange({ doseUnit: e.target.value })}
            maxLength={32}
            placeholder={row.medicineUnit}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Số lượng</Label>
          <div className="flex items-center gap-1.5">
            <Input
              type="number"
              inputMode="decimal"
              step="0.5"
              min="0"
              value={row.quantity ?? 0}
              onChange={(e) => onChange({ quantity: Number(e.target.value) || 0 })}
              className="flex-1"
            />
            {row.medicineUnit && (
              <span className="text-sm text-muted-foreground">{row.medicineUnit}</span>
            )}
          </div>
          {recommendedQuantity > 0 && row.quantity !== recommendedQuantity && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-0.5 text-[11px]"
              onClick={() => onChange({ quantity: recommendedQuantity })}
            >
              Tự tính {recommendedQuantity} {row.medicineUnit}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label>Đường dùng</Label>
          <select
            className={nativeSelectClass}
            value={row.route ?? ""}
            onChange={(e) => onChange({ route: e.target.value || null })}
          >
            <option value="">—</option>
            {routes.map((r) => (
              <option key={r.code} value={r.code}>
                {r.labelVi}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Bữa ăn</Label>
          <select
            className={nativeSelectClass}
            value={row.mealTiming ?? ""}
            onChange={(e) =>
              onChange({ mealTiming: (e.target.value || null) as MealTiming | null })
            }
          >
            <option value="">—</option>
            {(Object.keys(mealTimingLabel) as MealTiming[]).map((m) => (
              <option key={m} value={m}>
                {mealTimingLabel[m]}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Ghi chú</Label>
          <Input
            type="text"
            value={row.note ?? ""}
            onChange={(e) => onChange({ note: e.target.value || null })}
            maxLength={2000}
            placeholder="VD: dùng cách 6 giờ, ngừng khi hết triệu chứng"
          />
        </div>
      </div>
    </div>
  );
}
